import { _ } from "../core/polyglot/polyglot.js";
import Model from "./Model.js";

/**
 * A `dimension` can contain zero or more other dimensions, thus forming a
 * tree.
 *
 * @param {string} spec.name - Presentation name
 * @param {code} spec.code - User defined dimension code
 * @param {string} spec.id - Unique identifier
 * @param {boolean} spec.root - True if the dimension is the root of a dimension tree
 * @param {dimension[]} [spec.dimensions] - Sub dimensions
 */
export default class Dimension extends Model {
	constructor({
		name = "",
		code,
		id,
		root,
		dimensions = [],
		showDimensionCode = true,
	}) {
		super(...arguments);

		this._name = name;
		this._code = code;
		this._root = root;
		this._id = this._root ? "root" : id;
		this._dimensions = dimensions;
		this._showDimensionCode = showDimensionCode;
	}

	getChildren() {
		return this._dimensions.slice();
	}

	getLeaves() {
		if (this.isLeaf()) {
			return [this];
		}

		return this._dimensions.reduce((acc, dimension) => {
			acc.push(...dimension.getLeaves());
			return acc;
		}, []);
	}

	getName() {
		if (this._root) {
			return _("All Dimensions");
		}

		return this._name;
	}

	getCode() {
		return this._code;
	}

	getId() {
		return this._id;
	}

	getLabel() {
		if (this._root || !this._showDimensionCode) {
			return this.getName();
		}
		return `${this.getName()} (${this.getCode()})`;
	}

	findById(dimensionId) {
		if (this._id === dimensionId) {
			return this;
		}
		let result;
		for (let i = 0; i < this._dimensions.length; i++) {
			result = this._dimensions[i].findById(dimensionId);
			if (result) {
				return result;
			}
		}
		return null;
	}

	/**
	 * Lookup and return dimensions by id.  Ids for which no dimension can
	 * be found are ignored.
	 */
	findAllByIds(dimensionIds) {
		return dimensionIds
			.map((id) => this.findById(id))
			.filter((dimension) => dimension !== null);
	}

	/**
	 * Return a list of the receiver's dimensions that are included by
	 * `dimensionSpecification`.
	 */
	includedDimensions(dimensionSpecification) {
		if (dimensionSpecification && dimensionSpecification.canBeEdited()) {
			return this.findAllByIds(
				dimensionSpecification.includedDimensionIds(),
			);
		}

		return [];
	}

	/**
	 * Return a list of the receiver's dimensions that are excluded by
	 * `dimensionSpecification`.
	 */
	excludedDimensions(dimensionSpecification) {
		if (dimensionSpecification && dimensionSpecification.canBeEdited()) {
			return this.findAllByIds(
				dimensionSpecification.excludedDimensionIds(),
			);
		}

		return [];
	}

	/**
	 * Return an array of dimensions where:
	 *
	 * - the last element is the dimension corresponding to the parameter `childId` ;
	 *
	 * - the elements before are the parent dimensions of
	 *   `childId` up to and including the receiver (as first
	 *   element of the array).
	 *
	 * If `childId` is not a part of the receiver's dimension
	 * tree, return `undefined`.
	 */
	pathToChildId(childId) {
		if (this._id === childId) {
			return [this];
		}
		let result;
		for (let i = 0; i < this._dimensions.length; i++) {
			result = this._dimensions[i].pathToChildId(childId);
			if (result) {
				return [this].concat(result);
			}
		}
		return undefined;
	}

	/**
	 * Return a slash-separated string containing the dimension
	 * names from a receiver's child to the dimension
	 * corresponding to `childId`. This string can be seen as a
	 * path from the receiver to `childId`.
	 */
	pathNameToChildId(childId) {
		const path = this.pathToChildId(childId);
		switch (path.length) {
			case 0: {
				return "";
			}
			case 1: {
				return path[0].getName();
			}
			case 2: {
				return path[1].getName();
			}
			default: {
				return path
					.slice(1) // don't show the root
					.map((dimension) => dimension.getName())
					.join(" / ");
			}
		}
	}

	isRoot() {
		return !!this._root;
	}

	isLeaf() {
		return this._dimensions.length === 0;
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.name = this._name;
		properties.code = this._code;
		properties.id = this._id;
		properties.children = this._dimensions;

		return properties;
	}

	static transformMaterializationData(data, context) {
		data.root = context.root;
		data.showDimensionCode = context.showDimensionCode;

		context.root = false;

		data.dimensions = Dimension.materializeAll(data.children, context);
	}
}
